import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import { ImportJobsState } from '../../../redux/reducers/ImportJobs.Reducer';
import { actionCreators } from '../../../redux/middleware/ProviderConsumptions.Middleware';
import {
  mapDispatchToProps as mapImportDispatchToProps,
  ProviderConsumptionImportDispatch,
} from '../ProviderConsumptionImport/ProviderConsumptionImport.Connect';

// models
import {
  ProviderConsumption,
  ProviderConsumptionsFilter,
  ProviderConsumptionsPage,
  ProviderConsumptionTotals,
  ProviderSyncAlarmsFilter,
  ProviderSyncAlarmsPage,
  ProviderSyncRequestResult,
  ProviderSyncRun,
  ProviderToSettleRequest,
  ProviderToSettleResult,
  RevalueImportedRequest,
  RevalueImportedResult,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';

export interface ProviderConsumptionsOwnProps {
  /** Provider.id (int): lista, totales, sincronización, archivos e importación. */
  providerId: number;
  providerName: string;
  /** Provider.person.personIdentifier (Guid): identidad del alta de la liquidación de proveedor (spec 010). */
  providerIdentifier?: string;
  /** Orígenes iniciales del filtro (deep-link `?tab=importedConsumptions` → [5]). */
  initialOrigins?: number[];
  /** Avisa los orígenes elegidos para reflejarlos en la URL. */
  onOriginsChange?: (origins: number[]) => void;
  /** Archivo inicial del filtro (deep-link `?providerFileId=` del aviso "Ver consumos", spec 008); sólo con origen 5. */
  initialProviderFileId?: string;
  /** Abre el asistente de importación con este archivo (deep-link `?importFileId=` del aviso "Reintentar"). */
  initialImportFileId?: string;
  /** Se usó `initialImportFileId` (para limpiarlo de la URL). */
  onImportFileConsumed?: () => void;
}

/** Thunks de la grilla unificada (spec 006); todos rechazan con ErrorType. */
export interface ProviderConsumptionsGridDispatch {
  LoadProviderConsumptions: (providerId: number, filter: ProviderConsumptionsFilter) => Promise<ProviderConsumptionsPage>;
  LoadProviderConsumptionTotals: (providerId: number, filter: ProviderConsumptionsFilter) => Promise<ProviderConsumptionTotals>;
  LoadAllProviderConsumptions: (providerId: number, filter: ProviderConsumptionsFilter) => Promise<ProviderConsumption[]>;
  LoadLastProviderSync: (providerId: number) => Promise<ProviderSyncRun | null>;
  LoadProviderSyncRuns: (providerId: number, take?: number) => Promise<ProviderSyncRun[]>;
  LoadProviderSyncAlarms: (runId: string, filter: ProviderSyncAlarmsFilter) => Promise<ProviderSyncAlarmsPage>;
  RequestProviderSync: (providerId: number, protocolId?: number) => Promise<ProviderSyncRequestResult>;
  RevalueImportedConsumptions: (providerId: number, body?: RevalueImportedRequest) => Promise<RevalueImportedResult>;
  /** Marca / quita "Por liquidar" por ids (lotes de 1000) o por filtro (spec 010). */
  MarkProviderConsumptionsToSettle: (request: ProviderToSettleRequest) => Promise<ProviderToSettleResult>;
  /** Un consumo por id (GET protocols/providers/consumptions/{id}) para el drawer de detalle (spec 010, US8). */
  LoadProviderConsumption: (id: string) => Promise<ProviderConsumption | undefined>;
}

export type ProviderConsumptionsDispatch = ProviderConsumptionsGridDispatch & ProviderConsumptionImportDispatch;
export type ProviderConsumptionsProps = ProviderConsumptionsOwnProps & ProviderConsumptionsDispatch & ProviderConsumptionsStateProps;

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): ProviderConsumptionsDispatch => {
  const run = <T>(thunk: unknown): Promise<T> => dispatch(thunk as any) as unknown as Promise<T>;
  return {
    ...mapImportDispatchToProps(dispatch),
    LoadProviderConsumptions: (providerId, filter) => run(actionCreators.LoadProviderConsumptions(providerId, filter)),
    LoadProviderConsumptionTotals: (providerId, filter) => run(actionCreators.LoadProviderConsumptionTotals(providerId, filter)),
    LoadAllProviderConsumptions: (providerId, filter) => run(actionCreators.LoadAllProviderConsumptions(providerId, filter)),
    LoadLastProviderSync: (providerId) => run(actionCreators.LoadLastProviderSync(providerId)),
    LoadProviderSyncRuns: (providerId, take) => run(actionCreators.LoadProviderSyncRuns(providerId, take)),
    LoadProviderSyncAlarms: (runId, filter) => run(actionCreators.LoadProviderSyncAlarms(runId, filter)),
    RequestProviderSync: (providerId, protocolId) => run(actionCreators.RequestProviderSync(providerId, protocolId)),
    RevalueImportedConsumptions: (providerId, body) => run(actionCreators.RevalueImportedConsumptions(providerId, body)),
    MarkProviderConsumptionsToSettle: (request) => run(actionCreators.MarkProviderConsumptionsToSettle(request)),
    LoadProviderConsumption: (id) => run(actionCreators.LoadProviderConsumption(id)),
  };
};

/** Estado del aviso global (spec 008): jobs activos del proveedor (banner) y recarga al terminar. */
export interface ProviderConsumptionsStateProps {
  importJobsState?: ImportJobsState;
}

export const mapStateToProps = (state: AppState): ProviderConsumptionsStateProps => ({ importJobsState: state.ImportJobs });
